import i18n from '../../i18n';
import { prepareAndValidateEvidenceUpload } from '../../utils/mediaUpload';
import { apiErrorFromResponse } from '../../utils/apiErrors';
import { apiFetch, getApiBaseUrl } from './client';

export async function submitHeroicAction(payload) {
  return apiFetch('/heroic-actions', { method: 'POST', body: JSON.stringify(payload) });
}

export async function saveHeroicActionDraft(id, payload) {
  return apiFetch(id ? `/heroic-actions/drafts/${id}` : '/heroic-actions/drafts', { method: id ? 'PUT' : 'POST', body: JSON.stringify(payload) });
}

export async function submitHeroicActionDraft(id, payload) {
  return apiFetch(`/heroic-actions/drafts/${id}/submit`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function deleteHeroicActionDraft(id) {
  return apiFetch(`/heroic-actions/drafts/${id}`, { method: 'DELETE' });
}

export async function getMyHeroicActions(params = null) {
  if (!params) return apiFetch('/heroic-actions/mine');
  const query = new URLSearchParams();
  if (params.id) query.append('id', params.id);
  if (params.excludeDraft) query.append('excludeDraft', 'true');
  if (params.paged) { query.append('paged', 'true'); query.append('page', String(params.page || 1)); query.append('pageSize', String(params.pageSize || 30)); }
  if (params.search?.trim()) query.append('search', params.search.trim());
  if (params.categoryKey && params.categoryKey !== 'ALL') query.append('categoryKey', params.categoryKey);
  if (params.dateRange && params.dateRange !== 'ALL') query.append('dateRange', params.dateRange);
  if (params.dateRange === 'CUSTOM' && params.customDateFrom) query.append('dateFrom', params.customDateFrom);
  if (params.dateRange === 'CUSTOM' && params.customDateTo) query.append('dateTo', params.customDateTo);
  if (params.xpBand && params.xpBand !== 'ALL') query.append('xpBand', params.xpBand);
  if (params.status && params.status !== 'ALL') query.append('status', params.status);
  if (params.sort && params.sort !== 'DATE_DESC') query.append('sort', params.sort);

  const queryString = query.toString();
  return apiFetch(`/heroic-actions/mine${queryString ? `?${queryString}` : ''}`);
}

export async function getHeroicActionXpGuidance() {
  return apiFetch('/heroic-actions/xp-guidance');
}

export async function getPendingHeroicActions(params = {}) {
  return getHeroicActionsQueue('pending', params);
}

export async function getAllHeroicActions(params = {}) {
  return getHeroicActionsQueue('all', params);
}

async function getHeroicActionsQueue(scope, params) {
  const query = new URLSearchParams();
  if (params.id) query.append('id', params.id);
  if (params.paged) { query.append('paged', 'true'); query.append('page', String(params.page || 1)); query.append('pageSize', String(params.pageSize || 30)); }
  if (params.search) query.append('search', params.search);
  if (params.rank && params.rank !== 'ALL') query.append('rank', params.rank);
  if (params.xpBand && params.xpBand !== 'ALL') query.append('xpBand', params.xpBand);
  if (params.categoryKey && params.categoryKey !== 'ALL') query.append('categoryKey', params.categoryKey);
  if (params.difficulty && params.difficulty !== 'ALL') query.append('difficulty', params.difficulty);
  if (params.status && params.status !== 'ALL') query.append('status', params.status);

  const queryString = query.toString();
  return apiFetch(`/heroic-actions/${scope}${queryString ? `?${queryString}` : ''}`);
}

export async function reviewHeroicAction(id, payload) {
  return apiFetch(`/heroic-actions/${id}/review`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function restartHeroicActionReview(id, expectedReviewRound, expectedCompletedReviews, reason) {
  return apiFetch(`/heroic-actions/${id}/review/restart`, { method: 'POST', body: JSON.stringify({ expectedReviewRound, expectedCompletedReviews, reason }) });
}

export const previewHeroicActionXpRecalculation = id => apiFetch(`/heroic-actions/${id}/xp-recalculation`);
export const applyHeroicActionXpRecalculation = id => apiFetch(`/heroic-actions/${id}/xp-recalculation`, { method: 'POST' });

export async function returnHeroicAction(id, reason) {
  return apiFetch(`/heroic-actions/${id}/return`, { method: 'POST', body: JSON.stringify({ reason }) });
}

export async function applyForModerator(motivation) {
  return apiFetch('/heroic-actions/moderator/apply', { method: 'POST', body: JSON.stringify({ motivation }) });
}

export async function uploadHeroicActionEvidence(id, file) {
  const form = new FormData();
  form.append('file', await prepareAndValidateEvidenceUpload(file));
  form.append('privacyConfirmed', 'true');
  return apiFetch(`/heroic-actions/${id}/evidence`, { method: 'POST', body: form });
}

export async function getHeroicActionEvidence(id) {
  return apiFetch(`/heroic-actions/${id}/evidence`);
}

export const getHeroicActionReviewContext = id => apiFetch(`/heroic-actions/${id}/review-context`);

export async function downloadHeroicActionEvidence(id, evidenceId, signal) {
  const token = localStorage.getItem('goodvale_token');
  const response = await fetch(`${getApiBaseUrl()}/heroic-actions/${id}/evidence/${evidenceId}`, {
    signal,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw await apiErrorFromResponse(response, i18n);
  return response.blob();
}

export async function deleteHeroicActionEvidence(id, evidenceId) {
  return apiFetch(`/heroic-actions/${id}/evidence/${evidenceId}`, { method: 'DELETE' });
}

export async function createHeroicActionAppeal(id, reason) {
  return apiFetch(`/heroic-actions/${id}/appeals`, { method: 'POST', body: JSON.stringify({ reason }) });
}

export async function getPendingHeroicActionAppeals() {
  return apiFetch('/heroic-actions/appeals/pending');
}

export async function decideHeroicActionAppeal(appealId, approved, reason) {
  return apiFetch(`/heroic-actions/appeals/${appealId}/decision`, { method: 'POST', body: JSON.stringify({ approved, reason }) });
}

export async function getModeratorApplications() {
  return apiFetch('/heroic-actions/moderator/applications');
}

export async function getActiveModerators() {
  return apiFetch('/heroic-actions/moderator/moderators');
}

export async function reviewModeratorApplication(userId, payload) {
  return apiFetch(`/heroic-actions/moderator/applications/${userId}/review`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function revokeModeratorStatus(userId, reason) {
  return apiFetch(`/heroic-actions/moderator/${userId}/revoke`, { method: 'POST', body: JSON.stringify({ reason }) });
}

export const withdrawHeroicAction = id => apiFetch('/heroic-actions/' + encodeURIComponent(id) + '/withdraw', { method: 'POST' });
export const resubmitWithdrawnHeroicAction = (id, payload) => apiFetch('/heroic-actions/' + encodeURIComponent(id) + '/resubmit', { method: 'POST', body: JSON.stringify(payload) });

export async function downloadEvidenceThumbnail(id, evidenceId, signal) {
  const token = localStorage.getItem('goodvale_token');
  const response = await fetch(`${getApiBaseUrl()}/heroic-actions/${id}/evidence/${evidenceId}/thumbnail`, { signal, headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!response.ok) throw await apiErrorFromResponse(response, i18n);
  return response.blob();
}

export const getClassAbilityStatus = () => apiFetch('/heroic-actions/class-abilities');
export const skipHeroicActionCooldown = slotId => apiFetch(`/heroic-actions/cooldown-slots/${encodeURIComponent(slotId)}/skip`, { method: 'POST' });
export const repairHeroicAction = (id, payload) => apiFetch('/heroic-actions/' + id + '/repair', { method: 'POST', body: JSON.stringify(payload) });

export async function deleteHeroicAction(id, reverseRewards) {
  return apiFetch(`/heroic-actions/${id}`, { method: 'DELETE', body: JSON.stringify({ reverseRewards }) });
}
